import { useEffect, useRef, useState } from 'react'
import {
  ABLEHNUNGSGRUND_TEXT,
  type Ablehnungsgrund,
} from '../lib/warteschlangen'

/**
 * Was beim Ablehnen erhoben wird: **der Grund, und sonst nichts.**
 *
 * Er geht an den Menschen — nicht als Text, sondern als Code: Die App baut
 * daraus einen Satz in sechs Sprachen. Deshalb eine Auswahl und kein Freitext,
 * aus „- kein Spielplatzbild" wird kein französischer Satz.
 *
 * Eine interne Notiz stand hier bis zum 26.08.2026 daneben. Sie ist
 * gestrichen: In einer Schicht, die einen Stapel abarbeitet, ist ein
 * Pflichtfeld, das niemand liest, ein Aufenthalt. Die Spalte
 * `core_decision.reason` bleibt und bleibt leer — 220 von 234 Zeilen waren es
 * ohnehin schon.
 *
 * Die Wahl allein löst nichts aus. Eine Ablehnung nimmt Punkte und lässt sich
 * nicht zurücknehmen; ein verrutschter Klick auf eine Auswahlmarke darf das
 * nicht auslösen. Deshalb bleibt der zweite Schritt stehen, auch wenn er einen
 * Klick kostet.
 *
 * Ist die Liste leer, erscheint dieser Dialog gar nicht — dafür sorgt der
 * Stapel: Bei einer Meldung wird kein Beitrag abgelehnt, sondern festgestellt,
 * dass sie nicht zutrifft. Dann gibt es nichts zu erheben.
 */
export default function Begruendung({
  laeuft,
  gruende,
  abbrechen,
  absenden,
}: {
  laeuft: boolean
  /** Die zur Warteschlange passenden Gründe, die wahrscheinlichsten zuerst. */
  gruende: Ablehnungsgrund[]
  abbrechen: () => void
  absenden: (grundCode: Ablehnungsgrund) => void
}) {
  const [code, setCode] = useState<Ablehnungsgrund | null>(null)
  const ersterRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    ersterRef.current?.focus()
  }, [])

  return (
    <div className="karte begruendung">
      <h3>Warum abgelehnt?</h3>
      <p className="leise">
        Diese Auswahl bekommt der Mensch zu lesen — die App macht daraus einen
        Satz in seiner Sprache.
      </p>

      <div className="gruende" role="radiogroup" aria-label="Ablehnungsgrund">
        {gruende.map((g, i) => (
          <label key={g} className={g === code ? 'grund aktiv' : 'grund'}>
            <input
              ref={i === 0 ? ersterRef : undefined}
              type="radio"
              name="ablehnungsgrund"
              checked={g === code}
              onChange={() => setCode(g)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && code) {
                  e.preventDefault()
                  absenden(code)
                }
              }}
            />
            <span>{ABLEHNUNGSGRUND_TEXT[g]}</span>
          </label>
        ))}
      </div>

      <div className="knopf-reihe">
        <button
          className="knopf knopf-ab"
          type="button"
          disabled={!code || laeuft}
          onClick={() => code && absenden(code)}
        >
          Ablehnen
        </button>
        <button className="knopf knopf-still" type="button" onClick={abbrechen}>
          Abbrechen <kbd>Esc</kbd>
        </button>
        {!code && <p className="leise">Bitte einen Grund wählen.</p>}
      </div>
    </div>
  )
}
